import { Icon, Typography } from '@repo/ui';
import c from 'classnames';
import {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  Card,
  LinkButton,
  TextLink,
} from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { format } from 'date-fns';
import { parseDate } from 'helpers';
import useCheckoutKit from 'hooks/useCheckoutKit';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import useSubscriptions from 'services/general/subscriptionsList';
import {
  Subscription,
  SubscriptionStatus,
  SubscriptionType,
} from 'types/Subscription';
import { FadedCard } from '../FadedCard/FadedCard';
import styles from './MembershipSection.module.css';

export const MembershipSectionCard = ({
  subscription,
  tinyAccountId,
}: {
  subscription: Subscription;
  tinyAccountId: string | null;
}) => {
  const { latestKit } = useTinyAccountForHome(tinyAccountId);
  const { price, checkoutUrl } = useCheckoutKit({
    kitType: latestKit?.sampling_type,
  });
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs();
  const { trackEvent } = useAnalytics();
  const checkoutUrlWithUtm = `${checkoutUrl}&utm_source=tinyhealth&utm_medium=webapp&utm_campaign=benefits`;
  const hasUnusedKits =
    !!subscription.kits_count_total &&
    subscription.kits_count_used !== subscription.kits_count_total;

  if (!subscription || subscription.status !== SubscriptionStatus.ACTIVE)
    return <></>;

  return (
    <FadedCard>
      <div className='flex flex-column gap-3'>
        <div className={styles.cardHeader}>
          <Typography variant='heading-l'>
            {subscription.shopify_product_name ?? 'Your Tiny+ Membership'}
          </Typography>
          <div className='flex align-items-center gap-1'>
            <Icon name='award' size={16} color='grey' />
            <Typography variant='body-s' color='grey'>
              {subscription.date_next_charge_scheduled
                ? `Renews ${format(
                    parseDate(subscription.date_next_charge_scheduled),
                    'MM/dd/yyyy',
                  )}`
                : 'Annual'}
            </Typography>
          </div>
        </div>

        <div className={styles.grid}>
          <Card
            variant='wrapper'
            analyticsEventName='Schedule Call Clicked'
            clickable={{ source: 'homepage_membership', href: '/consults' }}
          >
            <div className={styles.cardContent}>
              <Typography variant='display-s' className={styles.quantity}>
                <span className={styles.quantityDark}>
                  {subscription.consult_credits_count_used ?? 0}
                </span>
                {` / ${subscription.consult_credits_count_total ?? 0}`}
              </Typography>
              <div className={c(styles.pill, styles.blue)}>
                <Typography variant='caption' className='my-auto'>
                  coaching sessions
                  <Typography variant='caption' className='hide-on-mobile'>
                    {` used`}
                  </Typography>
                </Typography>
              </div>

              <div
                className={c(
                  styles.cardActionsContainer,
                  'flex align-items-center justify-content-between gap-2 mt-1',
                )}
              >
                <Typography variant='body-s' className='dark-grey'>
                  $39/additional call
                </Typography>
                {subscription.subscription_type ===
                  SubscriptionType.TINY_PLUS && (
                  <div className={styles.actions}>
                    <TextLink
                      label='Book a call'
                      size='body-s'
                      iconNameRight='chevronForward'
                      to='/consults'
                      onClick={e => {
                        e.stopPropagation();
                        trackEvent('Schedule Call Clicked', {
                          source: 'homepage_membership',
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </Card>
          <Card
            variant='wrapper'
            clickable={{
              text: hasUnusedKits ? 'Activate kit' : 'Buy a kit',
              href: hasUnusedKits ? '/activate' : checkoutUrlWithUtm,
              isExternal: !hasUnusedKits,
              variety: 'Card',
            }}
          >
            <div className={styles.cardContent}>
              <Typography variant='display-s' className={styles.quantity}>
                <span className={styles.quantityDark}>
                  {subscription.kits_count_used ?? 0}
                </span>
                {` / ${subscription.kits_count_total ?? 0}`}
              </Typography>
              <div className={c(styles.pill, styles.purple)}>
                <Typography variant='caption'>
                  test kits
                  <Typography variant='caption' className='hide-on-mobile'>
                    {` used`}
                  </Typography>
                </Typography>
              </div>
              <div
                className={c(
                  styles.cardActionsContainer,
                  'flex align-items-center justify-content-between gap-2 mt-1',
                )}
              >
                <Typography variant='body-s' className='dark-grey'>
                  {`$${price}/additional kit`}
                </Typography>
                {(!portalConfig.hide_upsells || hasUnusedKits) &&
                  !isPortalConfigLoading && (
                    <div className={styles.actions}>
                      <TextLink
                        label={hasUnusedKits ? 'Activate kit' : 'Buy a kit'}
                        size='body-s'
                        iconNameRight='chevronForward'
                        contentOnly
                      />
                    </div>
                  )}
              </div>
            </div>
          </Card>
          <Card
            variant='wrapper'
            clickable={{
              text: 'Chat with us',
              href: '/help',
              isExternal: false,
              variety: 'Card',
            }}
            className={c(styles.cardContent, styles.big)}
          >
            <Typography variant='heading-l'>Chat with us</Typography>
            <div className={c(styles.pill, styles.green, 'mt-1 mb-1')}>
              <Typography variant='caption'>SMS and Email support</Typography>
            </div>
            <div className={c(styles.actions, 'mt-1 justify-content-center')}>
              <TextLink
                label='Message us'
                size='body-s'
                iconNameRight='chevronForward'
                contentOnly
              />
            </div>
          </Card>
        </div>
        {!portalConfig.hide_upsells && !isPortalConfigLoading && (
          <LinkButton
            label='Buy additional kits'
            href={checkoutUrlWithUtm}
            external
            variant={ButtonVariants.SOLID}
            size={ButtonSizes.M}
            color={ButtonColors.DARK_PURPLE}
            width={'full'}
          />
        )}
      </div>
    </FadedCard>
  );
};

export const MembershipSection = ({
  tinyAccountId,
}: {
  tinyAccountId: string | null;
}) => {
  const { data: subscriptionsList } = useSubscriptions();

  if (!subscriptionsList?.length) return <></>;
  return (
    <>
      {subscriptionsList.map(subscription => (
        <MembershipSectionCard
          tinyAccountId={tinyAccountId}
          subscription={subscription}
          key={subscription.id}
        />
      ))}
    </>
  );
};
